import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Steps,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloudUploadOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { bulkInventoryService, ProductsExcelParseResponse } from '../services/bulkInventoryService';
import { masterDataService, Category, Location } from '../services/masterDataService';
import { RBACService } from '../utils/rbac';
import { formatStockRange, parseStockRange } from '../utils/stockRange';
import { useResponsive } from '../hooks/useResponsive';
import './ProductExcelImport.css';

const { Title, Text } = Typography;
const { Dragger } = Upload;
const { Option } = Select;

export interface EditableImportRow {
  key: string;
  row_number: number;
  name: string;
  code: string;
  category_id: number;
  barcode: string;
  unit_of_measure: string;
  cost_price: number;
  selling_price: number;
  minimum_stock_level: number;
  maximum_stock_level: number;
  description: string;
  opening_quantity: number;
  warnings: string[];
}

function buildEditableRows(res: ProductsExcelParseResponse): EditableImportRow[] {
  const payload = res.create_payload || [];
  return payload.map((p, i) => {
    const vr = res.valid_rows[i];
    return {
      key: `r-${i}`,
      row_number: vr?.row_number ?? i + 2,
      name: String(p.name ?? '').trim(),
      code: String(p.code ?? '').trim(),
      category_id: Number(p.category_id),
      barcode: String(p.barcode ?? '').trim(),
      unit_of_measure: String(p.unit_of_measure ?? 'pcs').trim() || 'pcs',
      cost_price: Number(p.cost_price ?? 0),
      selling_price: Number(p.selling_price ?? 0),
      minimum_stock_level: Number(p.minimum_stock_level ?? 0),
      maximum_stock_level: Number(p.maximum_stock_level ?? 1000),
      description: String(p.description ?? '').trim(),
      opening_quantity: Math.max(0, Math.floor(Number(p.opening_quantity ?? 0) || 0)),
      warnings: vr?.warnings ?? [],
    };
  });
}

function categoryLabel(categories: Category[], id: number): string {
  const c = categories.find((x) => x.id === id);
  if (!c) return `ID ${id}`;
  if (c.parent_id) {
    const p = categories.find((x) => x.id === c.parent_id);
    return p ? `${p.name} › ${c.name}` : c.name;
  }
  return c.name;
}

const selectPopupContainer = (trigger: HTMLElement) => trigger.parentElement || document.body;

const ProductExcelImport: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile, isTablet } = useResponsive();
  const compactEditor = isMobile || isTablet;
  const [step, setStep] = useState(0);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [parseResult, setParseResult] = useState<ProductsExcelParseResponse | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [rows, setRows] = useState<EditableImportRow[]>([]);
  const [locationId, setLocationId] = useState<number | undefined>(undefined);
  const [locations, setLocations] = useState<Location[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  /** While typing min–max in a single field (e.g. 5 - 2000); cleared after successful blur parse. */
  const [stockRangeDraft, setStockRangeDraft] = useState<Record<string, string>>({});

  const user = RBACService.getCurrentUser();
  const defaultLoc = useMemo(() => {
    const n = Number(user?.locationId);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [user]);

  const loadMeta = useCallback(async () => {
    try {
      const [locs, cats] = await Promise.all([
        masterDataService.getLocations({ active_only: true, limit: 200 }),
        masterDataService.getCategories({ active_only: true, limit: 1000 }),
      ]);
      setLocations(locs);
      setCategories(cats);
    } catch {
      setLocations([]);
      setCategories([]);
    }
  }, []);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    if (defaultLoc && locationId === undefined && locations.some((l) => l.id === defaultLoc)) {
      setLocationId(defaultLoc);
    }
  }, [defaultLoc, locationId, locations]);

  useEffect(() => {
    setStockRangeDraft({});
  }, [fileName]);

  const updateRow = useCallback((key: string, patch: Partial<EditableImportRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }, []);

  const handleStockRangeChange = useCallback((key: string, value: string) => {
    setStockRangeDraft((d) => ({ ...d, [key]: value }));
  }, []);

  const handleStockRangeBlur = useCallback(
    (r: EditableImportRow, inputValue: string) => {
      const trimmed = inputValue.trim();
      const text = trimmed || formatStockRange(r.minimum_stock_level, r.maximum_stock_level);
      const p = parseStockRange(text);
      if (p) {
        updateRow(r.key, { minimum_stock_level: p.min, maximum_stock_level: p.max });
        setStockRangeDraft((d) => {
          const n = { ...d };
          delete n[r.key];
          return n;
        });
      } else if (trimmed) {
        message.warning(`Row ${r.row_number}: use two numbers, e.g. 5 - 2000`);
      } else {
        setStockRangeDraft((d) => {
          const n = { ...d };
          delete n[r.key];
          return n;
        });
      }
    },
    [updateRow],
  );

  const handleParseFile = async (file: File) => {
    setParsing(true);
    try {
      const res = await bulkInventoryService.uploadProductsExcel(file);
      setParseResult(res);
      setFileName(file.name);
      const next = buildEditableRows(res);
      if (!next.length) {
        message.warning('No valid rows to import. Fix the sheet or errors below and try again.');
        setStep(0);
        return;
      }
      setRows(next);
      setStep(1);
      message.success(`Loaded ${next.length} product row(s) from Excel. Review and choose a stock location.`);
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      message.error(typeof d === 'string' ? d : 'Could not read Excel file.');
    } finally {
      setParsing(false);
    }
  };

  const submitImport = async () => {
    if (!locationId) {
      message.error('Select a stock location for new inventory rows.');
      return;
    }
    const bad = rows.find((r) => !String(r.name || '').trim());
    if (bad) {
      message.error(`Row ${bad.row_number}: product name is required.`);
      return;
    }
    const badCat = rows.find((r) => !r.category_id || !Number.isFinite(r.category_id));
    if (badCat) {
      message.error(`Row ${badCat.row_number}: select a category.`);
      return;
    }
    setImporting(true);
    try {
      const items = rows.map((r) => ({
        name: String(r.name).trim(),
        code: String(r.code || '').trim(),
        category_id: r.category_id,
        barcode: String(r.barcode || '').trim(),
        unit_of_measure: String(r.unit_of_measure || 'pcs').trim() || 'pcs',
        cost_price: Number(r.cost_price) || 0,
        selling_price: Number(r.selling_price) || 0,
        minimum_stock_level: Math.max(0, Math.floor(Number(r.minimum_stock_level) || 0)),
        maximum_stock_level: Math.max(0, Math.floor(Number(r.maximum_stock_level) || 1000)),
        description: String(r.description || '').trim() || null,
        opening_quantity: Math.max(0, Math.floor(Number(r.opening_quantity) || 0)),
      }));
      const out = await bulkInventoryService.createProductsFromExcel({
        items,
        inventory_location_id: locationId,
      });
      if (out.created_count > 0) {
        message.success(`Created ${out.created_count} product(s).`);
      }
      if (out.failed_count > 0) {
        message.warning(`${out.failed_count} row(s) failed. Check the console or try again for those lines.`);
        console.warn('Import failures', out.failed);
      }
      navigate('/products');
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      message.error(typeof d === 'string' ? d : 'Import failed.');
    } finally {
      setImporting(false);
    }
  };

  const columns: ColumnsType<EditableImportRow> = useMemo(
    () => [
      { title: 'Sheet row', dataIndex: 'row_number', width: 88, fixed: 'left' },
      {
        title: 'Name',
        dataIndex: 'name',
        width: 200,
        render: (_, r) => (
          <Input
            value={r.name}
            size={compactEditor ? 'large' : 'middle'}
            onChange={(e) => updateRow(r.key, { name: e.target.value })}
          />
        ),
      },
      {
        title: 'Code',
        dataIndex: 'code',
        width: 120,
        render: (_, r) => (
          <Input
            value={r.code}
            size={compactEditor ? 'large' : 'middle'}
            placeholder="Auto if empty"
            onChange={(e) => updateRow(r.key, { code: e.target.value })}
          />
        ),
      },
      {
        title: 'Category',
        dataIndex: 'category_id',
        width: 220,
        render: (_, r) => (
          <Select
            showSearch
            size={compactEditor ? 'large' : 'middle'}
            optionFilterProp="children"
            style={{ width: '100%', minWidth: 120 }}
            value={r.category_id}
            onChange={(v) => updateRow(r.key, { category_id: v })}
            getPopupContainer={typeof document !== 'undefined' ? selectPopupContainer : undefined}
            dropdownStyle={{ maxHeight: 280 }}
            virtual={false}
          >
            {categories.map((c) => (
              <Option key={c.id} value={c.id}>
                {categoryLabel(categories, c.id)}
              </Option>
            ))}
          </Select>
        ),
      },
      {
        title: 'Barcode',
        dataIndex: 'barcode',
        width: 140,
        render: (_, r) => (
          <Input
            value={r.barcode}
            size={compactEditor ? 'large' : 'middle'}
            onChange={(e) => updateRow(r.key, { barcode: e.target.value })}
          />
        ),
      },
      {
        title: 'UOM',
        dataIndex: 'unit_of_measure',
        width: 90,
        render: (_, r) => (
          <Input
            value={r.unit_of_measure}
            size={compactEditor ? 'large' : 'middle'}
            onChange={(e) => updateRow(r.key, { unit_of_measure: e.target.value })}
          />
        ),
      },
      {
        title: 'Cost',
        dataIndex: 'cost_price',
        width: 110,
        render: (_, r) => (
          <InputNumber
            min={0}
            size={compactEditor ? 'large' : 'middle'}
            style={{ width: '100%' }}
            value={r.cost_price}
            onChange={(v) => updateRow(r.key, { cost_price: Number(v) || 0 })}
          />
        ),
      },
      {
        title: 'Sell',
        dataIndex: 'selling_price',
        width: 110,
        render: (_, r) => (
          <InputNumber
            min={0}
            size={compactEditor ? 'large' : 'middle'}
            style={{ width: '100%' }}
            value={r.selling_price}
            onChange={(v) => updateRow(r.key, { selling_price: Number(v) || 0 })}
          />
        ),
      },
      {
        title: 'Min – max stock',
        key: 'minmax',
        width: 140,
        render: (_, r) => (
          <Input
            size={compactEditor ? 'middle' : 'small'}
            style={{ width: '100%' }}
            placeholder="5 - 2000"
            value={stockRangeDraft[r.key] ?? formatStockRange(r.minimum_stock_level, r.maximum_stock_level)}
            onChange={(e) => handleStockRangeChange(r.key, e.target.value)}
            onBlur={(e) => handleStockRangeBlur(r, e.target.value)}
          />
        ),
      },
      {
        title: 'Opening qty',
        dataIndex: 'opening_quantity',
        width: 110,
        render: (_, r) => (
          <InputNumber
            min={0}
            size={compactEditor ? 'large' : 'middle'}
            style={{ width: '100%' }}
            value={r.opening_quantity}
            onChange={(v) => updateRow(r.key, { opening_quantity: Math.max(0, Math.floor(Number(v) || 0)) })}
          />
        ),
      },
      {
        title: 'Description',
        dataIndex: 'description',
        width: 180,
        render: (_, r) => (
          <Input
            value={r.description}
            size={compactEditor ? 'large' : 'middle'}
            onChange={(e) => updateRow(r.key, { description: e.target.value })}
          />
        ),
      },
      {
        title: 'Notes',
        key: 'warnings',
        width: 200,
        render: (_, r) =>
          r.warnings?.length ? (
            <Space wrap size={[4, 4]}>
              {r.warnings.map((w) => (
                <Tag key={w} color="warning" style={{ margin: 0 }}>
                  {w}
                </Tag>
              ))}
            </Space>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
    ],
    [categories, compactEditor, updateRow, stockRangeDraft, handleStockRangeChange, handleStockRangeBlur],
  );

  const mobileRowCard = (r: EditableImportRow) => (
    <Card key={r.key} size="small" className="product-excel-import-row-card" bordered>
      <div className="product-excel-import-row-card__head">
        <Tag>Sheet row {r.row_number}</Tag>
        {r.warnings?.length ? (
          <Space wrap size={[4, 4]} className="product-excel-import-row-card__warn">
            {r.warnings.map((w) => (
              <Tag key={w} color="warning">
                {w}
              </Tag>
            ))}
          </Space>
        ) : null}
      </div>
      <Row gutter={[12, 12]}>
        <Col span={24}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Name
          </Text>
          <Input size="large" value={r.name} onChange={(e) => updateRow(r.key, { name: e.target.value })} />
        </Col>
        <Col xs={24} sm={12}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Code
          </Text>
          <Input
            size="large"
            value={r.code}
            placeholder="Auto if empty"
            onChange={(e) => updateRow(r.key, { code: e.target.value })}
          />
        </Col>
        <Col xs={24} sm={12}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Barcode
          </Text>
          <Input size="large" value={r.barcode} onChange={(e) => updateRow(r.key, { barcode: e.target.value })} />
        </Col>
        <Col span={24}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Category
          </Text>
          <Select
            showSearch
            size="large"
            optionFilterProp="children"
            style={{ width: '100%' }}
            value={r.category_id}
            onChange={(v) => updateRow(r.key, { category_id: v })}
            getPopupContainer={typeof document !== 'undefined' ? () => document.body : undefined}
            dropdownStyle={{ maxHeight: 320 }}
          >
            {categories.map((c) => (
              <Option key={c.id} value={c.id}>
                {categoryLabel(categories, c.id)}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={8} sm={6}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            UOM
          </Text>
          <Input size="large" value={r.unit_of_measure} onChange={(e) => updateRow(r.key, { unit_of_measure: e.target.value })} />
        </Col>
        <Col xs={8} sm={9}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Cost
          </Text>
          <InputNumber
            min={0}
            size="large"
            style={{ width: '100%' }}
            value={r.cost_price}
            onChange={(v) => updateRow(r.key, { cost_price: Number(v) || 0 })}
          />
        </Col>
        <Col xs={8} sm={9}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Sell
          </Text>
          <InputNumber
            min={0}
            size="large"
            style={{ width: '100%' }}
            value={r.selling_price}
            onChange={(v) => updateRow(r.key, { selling_price: Number(v) || 0 })}
          />
        </Col>
        <Col span={24}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Min – max stock (e.g. 5 - 2000)
          </Text>
          <Input
            size="large"
            style={{ width: '100%' }}
            placeholder="5 - 2000"
            value={stockRangeDraft[r.key] ?? formatStockRange(r.minimum_stock_level, r.maximum_stock_level)}
            onChange={(e) => handleStockRangeChange(r.key, e.target.value)}
            onBlur={(e) => handleStockRangeBlur(r, e.target.value)}
          />
        </Col>
        <Col span={24}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Opening qty (at selected location)
          </Text>
          <InputNumber
            min={0}
            size="large"
            style={{ width: '100%' }}
            value={r.opening_quantity}
            onChange={(v) => updateRow(r.key, { opening_quantity: Math.max(0, Math.floor(Number(v) || 0)) })}
          />
        </Col>
        <Col span={24}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Description
          </Text>
          <Input size="large" value={r.description} onChange={(e) => updateRow(r.key, { description: e.target.value })} />
        </Col>
      </Row>
    </Card>
  );

  return (
    <div className="product-excel-import-page">
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }} gutter={[16, 16]}>
        <Col>
          <Space align="start">
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/products')}>
              Back to products
            </Button>
            <div>
              <Title level={3} style={{ margin: 0 }}>
                <ShoppingOutlined /> Import products from Excel
              </Title>
              <Text type="secondary">Upload a vendor sheet, then choose where stock rows are created and fix any cells.</Text>
            </div>
          </Space>
        </Col>
      </Row>

      <Steps current={step} style={{ marginBottom: 24 }} responsive>
        <Steps.Step title="Upload Excel" description=".xlsx / .xls" />
        <Steps.Step title="Location & review" description="Edit rows, then import" />
      </Steps>

      {step === 0 && (
        <Card>
          <Dragger
            accept=".xlsx,.xls"
            showUploadList={false}
            disabled={parsing}
            beforeUpload={(file) => {
              void handleParseFile(file as File);
              return false;
            }}
          >
            <p className="ant-upload-drag-icon">
              <CloudUploadOutlined />
            </p>
            <p className="ant-upload-text">Click or drag Excel file here</p>
            <p className="ant-upload-hint">
              Optional column <strong>Opening quantity</strong> (or On hand / Stock on hand) sets on-hand stock at the
              location you pick next. Extra unmapped columns are ignored. You can edit all fields before import.
            </p>
          </Dragger>
          {parseResult && !parseResult.create_payload?.length ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
              message="No importable rows"
              description={
                parseResult.invalid_rows?.length ? (
                  <ul style={{ maxHeight: 200, overflow: 'auto', margin: '8px 0 0', paddingLeft: 20 }}>
                    {parseResult.invalid_rows.slice(0, 30).map((ir) => (
                      <li key={ir.row_number}>
                        Row {ir.row_number}: {ir.error || 'Invalid'}
                      </li>
                    ))}
                  </ul>
                ) : (
                  'Fix the sheet and upload again.'
                )
              }
              action={
                <Button size="small" onClick={() => setParseResult(null)}>
                  Dismiss
                </Button>
              }
            />
          ) : null}
        </Card>
      )}

      {step === 1 && parseResult && (
        <>
          {parseResult.parse_notes?.length ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="How the file was read"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {parseResult.parse_notes.map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
              }
            />
          ) : null}
          {parseResult.ignored_columns?.length ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={`Ignored ${parseResult.ignored_columns.length} extra column(s) (not mapped)`}
              description={parseResult.ignored_columns.join(', ')}
            />
          ) : null}
          {parseResult.invalid_rows?.length ? (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 12 }}
              message={`${parseResult.invalid_rows.length} row(s) skipped from the sheet`}
              description={
                <ul style={{ maxHeight: 160, overflow: 'auto', margin: 0, paddingLeft: 20 }}>
                  {parseResult.invalid_rows.slice(0, 50).map((ir) => (
                    <li key={ir.row_number}>
                      Row {ir.row_number}: {ir.error || 'Invalid'}
                    </li>
                  ))}
                  {parseResult.invalid_rows.length > 50 ? <li>…</li> : null}
                </ul>
              }
            />
          ) : null}

          <Card title="Stock location" style={{ marginBottom: 16 }} className="product-excel-import-location-card">
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Text>
                Each new product gets an inventory row at this location. Use the <strong>Opening qty</strong> column in
                the table for on-hand units at this branch; leave 0 if you only want an empty row (then use Stock
                Adjustment or GRPO). Import with opening quantity requires <strong>stock adjustment create</strong>{' '}
                permission.
              </Text>
              <Select
                placeholder="Select location"
                style={{ maxWidth: 400, width: '100%' }}
                value={locationId}
                onChange={(v) => setLocationId(v)}
                showSearch
                optionFilterProp="children"
              >
                {locations.map((l) => (
                  <Option key={l.id} value={l.id}>
                    {l.name}
                  </Option>
                ))}
              </Select>
            </Space>
          </Card>

          <Card
            title={
              <Space>
                <span>Products to create ({rows.length})</span>
                {fileName ? <Tag>{fileName}</Tag> : null}
              </Space>
            }
            extra={
              <Space>
                <Button onClick={() => { setStep(0); setParseResult(null); setRows([]); }}>Upload different file</Button>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  loading={importing}
                  disabled={!rows.length || !locationId}
                  onClick={() => void submitImport()}
                >
                  Import {rows.length} product(s)
                </Button>
              </Space>
            }
          >
            <Table<EditableImportRow>
              size="small"
              rowKey="key"
              columns={columns}
              dataSource={rows}
              pagination={{ pageSize: 20, showSizeChanger: true }}
              scroll={{ x: 1720 }}
            />
          </Card>
        </>
      )}
    </div>
  );
};

export default ProductExcelImport;
